import React from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { adminApi } from '../../../lib/adminAuth';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import CategoryReviewList from './CategoryReviewList';

const TABS = [
  { key: 'retainer', label: 'Should be Retainer', action: 'Change to Retainer', dismiss: 'Not a retainer' },
  { key: 'non_retainer', label: 'Should be Non-retainer', action: 'Change to Non-retainer', dismiss: 'Keep as retainer' },
];
const LEVELS = ['all', 'high', 'medium', 'low'];
const defaultPick = (data) => new Set(TABS.flatMap((t) => (data?.[t.key] || []).filter((r) => r.confidence !== 'low').map((r) => r._id)));

export default function CategoryReviewDialog({ open, data, onClose, onDone }) {
  const [tab, setTab] = React.useState('retainer');
  const [level, setLevel] = React.useState('all');
  const [hideLow, setHideLow] = React.useState(false);
  const [selected, setSelected] = React.useState(() => defaultPick(data));
  const [confirming, setConfirming] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const wasOpen = React.useRef(false);
  React.useEffect(() => {
    if (open && !wasOpen.current) {
      setSelected(defaultPick(data));
      setConfirming(false);
      setTab((data?.retainer || []).length || !(data?.non_retainer || []).length ? 'retainer' : 'non_retainer');
    }
    wasOpen.current = open;
  }, [open, data]);

  const meta = TABS.find((t) => t.key === tab);
  const all = data?.[tab] || [];
  const rows = all.filter((r) => (level === 'all' || r.confidence === level) && !(hideLow && r.confidence === 'low'));
  const picked = rows.filter((r) => selected.has(r._id)).map((r) => r._id);
  const hiddenPicked = all.filter((r) => selected.has(r._id)).length - picked.length;
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const setMany = (ids, on) => setSelected((s) => { const n = new Set(s); ids.forEach((id) => (on ? n.add(id) : n.delete(id))); return n; });

  const post = async (path, okMsg) => {
    setBusy(true);
    try {
      const r = await adminApi.post(`/api/admin/accounting/invoice-category-review/${path}`, { invoice_ids: picked, target: tab });
      toast.success(okMsg(r.data));
      setConfirming(false);
      setMany(picked, false);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col rounded-2xl" data-testid="category-review-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1a1a2e]">Review invoice categories</DialogTitle>
          <DialogDescription>Only the category changes; amounts, numbers, GST and PDFs stay the same. Every change is logged in the Audit Trail.</DialogDescription>
        </DialogHeader>
        <div className="flex p-1 bg-slate-100 rounded-xl w-fit">
          {TABS.map((t) => (
            <button key={t.key} type="button" onClick={() => { setTab(t.key); setConfirming(false); }}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${tab === t.key ? 'bg-white text-fuchsia-700 shadow-sm' : 'text-slate-600'}`}
              data-testid={`category-review-tab-${t.key}`}>{t.label} ({(data?.[t.key] || []).length})</button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {LEVELS.map((l) => (
            <button key={l} type="button" onClick={() => setLevel(l)}
              className={`px-2.5 py-1 rounded-full border font-semibold capitalize ${level === l ? 'bg-fuchsia-600 text-white border-fuchsia-600' : 'bg-white text-slate-600 border-slate-200 hover:border-fuchsia-300'}`}
              data-testid={`category-review-level-${l}`}>{l}</button>
          ))}
          <label className="inline-flex items-center gap-1.5 ml-1 text-slate-600 cursor-pointer">
            <input type="checkbox" checked={hideLow} onChange={(e) => setHideLow(e.target.checked)} className="accent-fuchsia-600" data-testid="category-review-hide-low" /> Hide low
          </label>
          <span className="ml-auto flex gap-2">
            <button type="button" onClick={() => setMany(rows.map((r) => r._id), true)} className="font-semibold text-fuchsia-700 hover:underline" data-testid="category-review-select-all">Select all</button>
            <button type="button" onClick={() => setMany(all.map((r) => r._id), false)} className="font-semibold text-slate-600 hover:underline" data-testid="category-review-select-none">None</button>
          </span>
        </div>
        <div className="flex-1 overflow-y-auto min-h-[200px]">
          <CategoryReviewList rows={rows} selected={selected} onToggle={toggle} />
        </div>
        {confirming ? (
          <div className="flex flex-wrap items-center gap-2 p-3 rounded-xl bg-fuchsia-50 border border-fuchsia-200" data-testid="category-review-confirm-bar">
            <p className="text-sm text-fuchsia-900 flex-1">Change <b>{picked.length}</b> invoice{picked.length !== 1 ? 's' : ''} to <b>{tab === 'retainer' ? 'Retainer' : 'Non-retainer'}</b>?</p>
            <Button variant="outline" size="sm" onClick={() => setConfirming(false)} disabled={busy} data-testid="category-review-confirm-back">Back</Button>
            <Button size="sm" onClick={() => post('apply', (d) => `${d.changed.length} invoice(s) updated`)} disabled={busy}
              className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="category-review-confirm-apply">
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Confirm change
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <span className="text-xs text-slate-500 mr-auto" data-testid="category-review-selected-count">{picked.length} selected{hiddenPicked > 0 ? ` · ${hiddenPicked} ticked but hidden by filter (not included)` : ''}</span>
            <Button variant="outline" size="sm" disabled={!picked.length || busy} onClick={() => post('dismiss', (d) => `${d.dismissed} hidden from future suggestions`)} data-testid="category-review-dismiss">
              {meta.dismiss}
            </Button>
            <Button size="sm" disabled={!picked.length || busy} onClick={() => setConfirming(true)} className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="category-review-apply">
              {meta.action} ({picked.length})
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
